(function () {
  'use strict';

  // Reconciles the SERVER-PAINTED sale price against the offer the cart will
  // actually arm.
  //
  // The server paints struck prices from the site-wide DEFAULT offer
  // (snippets/offer-product-sale.liquid). The money comes from a discount CODE
  // armed by offer-reward.js off window.__activeOffer — the session / trigger /
  // url offer, resolved in the browser (offer-resolver.js resolveOffer). Those
  // are different objects, and with the shipped registry they diverge on three
  // ordinary paths:
  //
  //   • a builder bundle in the cart — `bundle` is an OVERRIDE trigger, so it
  //     beats the session and arms its own tier code (15%) while every PDP was
  //     painted at the sale's 20%
  //   • a deliberate session — a blackout landing keeps its own offer, because a
  //     dated default may only supersede a session acquired AMBIENTLY
  //   • ?offer=<handle> — an explicit override, message-only offers included
  //
  // No server can know any of that: the paint is page-global and cacheable by
  // design, the arm is per-session. So the paint is a CLAIM the client has to
  // check, exactly like the announcement bar's copy (offer-announcement.js) —
  // same event, same shape, same reason.
  //
  // DIRECTION IS THE WHOLE POINT. Reverting means a customer occasionally sees
  // list price and is charged less at checkout — a pleasant surprise. Not
  // reverting means showing 20% off and charging full price, which is the
  // support-ticket-and-chargeback direction. So: fail CLOSED. Anything unknown —
  // no offer resolved, a resolver that never published, an offer with no code —
  // reverts.

  // .auto-discount-price is emitted by exactly one place — the SALE BRANCH of
  // auto-discount-price.liquid — and rebuilt by exactly one other, theme.js
  // _.updatePrice:4507. The rebuild does NOT carry data-offer-sale forward, so
  // matching on the marker attribute would find nothing the moment a shopper
  // picked a colour and the paint would survive unreverted. Match on the class;
  // read the marker separately, and remember it.
  var SALE_SELECTOR = '.auto-discount-price';
  var PAINT_ATTR = 'data-offer-sale';
  // theme.js reads this off the product form to decide whether to repaint. It is
  // 'true' today only when offer-product-sale.liquid said the active offer prices
  // this product, so flipping it is precisely disarming the paint.
  var FORM_SELECTOR = '[data-product-form][data-auto-discount="true"]';
  var FALLBACK_MS = 5000;
  var resolved = false;

  // The painted offer, captured from the SERVER render before any variant switch
  // can strip it. Page-global by construction: the paint comes from the default
  // offer, which is the same for every product on the page.
  var paintedOfferId = '';

  function readPaintedId(scope) {
    var el = (scope || document).querySelector('[' + PAINT_ATTR + ']');
    return el ? el.getAttribute(PAINT_ATTR) || '' : '';
  }

  // The offer whose code offer-reward.js will arm. Mirrors activeOfferCode's
  // source (window.__activeOffer), not the session layer: a cart-content campaign
  // displaces the session for arming purposes, and that displacement is exactly
  // one of the cases we are here to catch.
  function armedOfferId() {
    var o = window.__activeOffer;
    return o && o.id ? String(o.id) : '';
  }

  // Exported for unit tests, and kept free of DOM so the rule can be read in one
  // place: the paint stands only when the offer that painted it is the offer
  // being armed, and that offer actually carries a code to arm.
  function paintHolds(paintedId, activeOffer) {
    if (!paintedId) return false;
    if (!activeOffer || !activeOffer.id) return false;
    if (String(activeOffer.id) !== String(paintedId)) {
      // CO-ARM CASE (2026-08-24, Labor Day eval MED-3): a collection_qty
      // campaign displaced the display slot, but the painted offer is still
      // the SESSION sale and its code still co-arms (offer-reward
      // sessionSaleCode admits exactly this shape). Without this, one sconce
      // in the cart un-painted the 20% on every lamp for the whole session -
      // under-advertising while the discount still applied. Mirrors
      // SALE_RIDES_ALONGSIDE; widen one, widen both.
      var sess = (typeof window !== 'undefined') && window.__sessionOffer;
      var coArm = sess && String(sess.id) === String(paintedId) &&
        activeOffer.trigger && activeOffer.trigger.type === 'collection_qty' &&
        sess.reward && typeof sess.reward.code === 'string' && sess.reward.code.trim();
      return !!coArm;
    }
    var reward = activeOffer.reward || {};
    var code = typeof reward.code === 'string' ? reward.code.trim() : '';
    return !!code;
  }

  // Revert to list price using the ALREADY-RENDERED original, not a re-format.
  // .auto-discount-price__original holds the list price in the store's money
  // format, produced by the same Liquid `price` snippet as every other price on
  // the page; re-formatting from data-original-price would depend on theme.js
  // money helpers being loaded and could disagree by a currency symbol.
  function revert(el) {
    var original = el.querySelector('.auto-discount-price__original');
    var span = document.createElement('span');
    span.className = 'theme-money';
    if (original) span.innerHTML = original.innerHTML;
    else span.textContent = '';
    if (el.parentNode) el.parentNode.replaceChild(span, el);
  }

  // theme.js _.updatePrice REBUILDS the price area from the product form's
  // data-auto-discount* attributes on every variant switch, so reverting the
  // rendered markup alone would last exactly until the shopper picked a colour.
  // Flip the attribute the rebuild gates on.
  function disarmForms(root) {
    (root || document).querySelectorAll(FORM_SELECTOR).forEach(function (form) {
      form.setAttribute('data-auto-discount', 'false');
    });
  }

  function sync(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll(SALE_SELECTOR);
    var forms = scope.querySelectorAll(FORM_SELECTOR);
    if (!nodes.length && !forms.length) return;
    // Prefer a marker inside this scope — ajax-injected markup (quick view, a
    // recommendations block) carries its own, and it is the fresher truth. Fall
    // back to the page's, which survives the variant-switch rebuild.
    var painted = readPaintedId(scope) || paintedOfferId;
    if (paintHolds(painted, window.__activeOffer)) return;
    disarmForms(scope);
    nodes.forEach(revert);
  }

  // ── SESSION PAINT (Sean 9/10: "if it is active in session, paint all the pricing for
  // discounted items on every surface") ──
  // The server paints only the site default (or a page-owned offer on its own page). A
  // session that acquired a landing-only offer (a blackout page) would otherwise browse
  // full prices while the cart discounts. Same gate as offer-product-sale.liquid: an
  // unconditional discount_code offer with a live code, a percent, and a collection scope.
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  function sessionPaintOffer() {
    var o = window.__activeOffer; if (!o || !o.id) return null;
    var t = o.trigger || {}, r = o.reward || {};
    if ((t.type || 'none') !== 'none') return null;
    if (r.type !== 'discount_code' || !r.code) return null;
    var pct = parseInt(r.pct, 10) || 0; if (pct <= 0 || !t.collection) return null;
    var end = (o.window && o.window.end) || '';
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(end); // display only; never date math (decisions 2026-08-11)
    return { id: o.id, pct: pct, collection: t.collection, event: o.event || '', ends: m ? MONTHS[parseInt(m[2], 10) - 1] + ' ' + parseInt(m[3], 10) : '' };
  }
  function money(cents) {
    if (window.theme && theme.Shopify && theme.Shopify.formatMoney) return theme.Shopify.formatMoney(cents, theme.money_format || '${{amount}}');
    return '$' + (cents / 100).toFixed(2);
  }
  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  function paintNode(el, offer) {
    var cents = parseInt(el.getAttribute('data-price-cents'), 10); if (!cents) return false;
    if (el.hasAttribute('data-gift')) return false;
    var cols = (el.getAttribute('data-collections') || '').split(',');
    if (cols.indexOf(offer.collection) < 0) return false;
    var disc = Math.round(cents * (100 - offer.pct) / 100), save = cents - disc;
    var wrap = document.createElement('span');
    wrap.className = 'auto-discount-price';
    wrap.setAttribute('data-auto-discount', 'true');
    wrap.setAttribute(PAINT_ATTR, offer.id);
    wrap.setAttribute('data-session-paint', '1');
    wrap.setAttribute('data-auto-discount-percentage', offer.pct);
    wrap.setAttribute('data-original-price', cents);
    wrap.setAttribute('data-discounted-price', disc);
    if (offer.event) { wrap.setAttribute('data-event-name', offer.event); wrap.setAttribute('data-event-ends', offer.ends); }
    // same order as auto-discount-price.liquid: current, strike, one badge, event line
    wrap.innerHTML = '<span class="auto-discount-price__discounted current-price theme-money">' + money(disc) + '</span>'
      + '<span class="auto-discount-price__original was-price theme-money">' + money(cents) + '</span>'
      + (save > 0 ? '<span class="auto-discount-badge auto-discount-badge--savings status-chip">Save ' + money(save) + '</span>'
                  : '<span class="auto-discount-badge status-chip">' + offer.pct + '% OFF</span>')
      + (offer.event ? '<span class="sale-event-line"><span class="sale-event-line__name">' + esc(offer.event) + '</span>'
          + (offer.ends ? '<span class="sale-event-line__ends">Ends ' + esc(offer.ends) + '</span>' : '') + '</span>' : '');
    el.parentNode.replaceChild(wrap, el);
    return true;
  }
  function paint(scope, offer) {
    var painted = 0;
    scope.querySelectorAll('.offer-paintable').forEach(function (el) {
      if (paintNode(el, offer)) {
        painted++;
        // the PDP rebuild (theme.js updatePrice) reads the FORM's attrs on every variant
        // switch: arm them so the paint survives the switch, exactly what disarmForms undoes
        var area = el.closest ? el.closest('.product-area') : null;
        var form = area && area.querySelector('[data-product-form]');
        if (form) {
          form.setAttribute('data-auto-discount', 'true');
          form.setAttribute('data-auto-discount-percentage', String(offer.pct));
          form.setAttribute('data-auto-discount-badge-text', offer.pct + '% OFF');
        }
      }
    });
    // PDP: theme.js rebuilds the price area at load from the FORM's attrs, before the
    // session resolves, so no paintable survives there. Paint from the form instead.
    scope.querySelectorAll('[data-product-form][data-offer-collections]').forEach(function (form) {
      if (form.getAttribute('data-auto-discount') === 'true') return;
      if ((form.getAttribute('data-offer-collections') || '').split(',').indexOf(offer.collection) < 0) return;
      var cents = parseInt(form.getAttribute('data-price-cents'), 10); if (!cents) return;
      form.setAttribute('data-auto-discount', 'true');
      form.setAttribute('data-auto-discount-percentage', String(offer.pct));
      form.setAttribute('data-auto-discount-badge-text', offer.pct + '% OFF');
      var area = form.closest('.product-area') || document;
      area.querySelectorAll('.price-area').forEach(function (pa) {
        if (pa.querySelector(SALE_SELECTOR)) return;
        var cur = pa.querySelector('.theme-money:not(.was-price)'); if (!cur) return;
        var shim = document.createElement('span');
        shim.setAttribute('data-price-cents', String(cents));
        shim.setAttribute('data-collections', form.getAttribute('data-offer-collections'));
        cur.parentNode.replaceChild(shim, cur);
        pa.querySelectorAll('.was-price').forEach(function (w) { if (!w.closest(SALE_SELECTOR)) w.remove(); });
        if (paintNode(shim, offer)) painted++;
      });
    });
    if (painted) paintedOfferId = offer.id; // the rebuild path then holds the paint (paintHolds)
    return painted;
  }

  function onResolved() {
    resolved = true;
    sync();
    var so = sessionPaintOffer();
    if (so) paint(document, so);
  }

  window.OfferPriceSync = {
    sync: sync,
    paint: function (root) { var so = sessionPaintOffer(); return so ? paint(root || document, so) : 0; },
    paintHolds: paintHolds,
    // The load-time marker. Readable so a test can assert the capture happened,
    // writable so the post-rebuild path — the one where theme.js has already
    // stripped the marker out of the DOM — can be driven without provisioning a
    // live sale on the store. That path is the money-critical one; a getter-only
    // export would have left it verified by nothing until September.
    paintedOffer: function (set) {
      if (typeof set === 'string') paintedOfferId = set;
      return paintedOfferId;
    },
  };

  if (window.__offerPriceSyncBound) return; // editor section re-render guard
  window.__offerPriceSyncBound = true;
  // Capture BEFORE anything can rebuild the wrapper. This script is deferred, so
  // the document is parsed and the server's marker is present; theme.js cannot
  // have run a variant switch yet because that needs a user interaction.
  paintedOfferId = readPaintedId(document);
  document.addEventListener('offer:resolved', onResolved);
  if (window.__activeOffer !== undefined) onResolved();

  // A resolver that never publishes is not a reason to trust the paint — it is
  // the strongest reason not to: nothing published means nothing armed, so the
  // struck price is certainly a lie. Bounded wait, then fail closed. The visible
  // cost is a price flicker on a session where the offer system is already
  // broken; the alternative cost is charging full price for an advertised sale.
  setTimeout(function () {
    if (!resolved) sync();
  }, FALLBACK_MS);
})();
